import { Ticket, Link2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { deriveExternalPlatform, externalPlatformName } from '@/lib/externalPlatform'

export type PaymentMode = 'native' | 'external'
type CampaignKind = 'event' | 'fundraiser'

const NATIVE_COPY: Record<CampaignKind, { title: string; description: string; bullets: string[] }> = {
  event: {
    title: 'Use Gala Season ticketing',
    description: 'Sell tickets and collect RSVPs right on this page.',
    bullets: ['Tickets and RSVP', 'Guest list built in', 'Nothing to set up elsewhere'],
  },
  fundraiser: {
    title: 'Use Gala Season donations',
    description: 'Accept donations directly on this page.',
    bullets: ['Native donations', 'Progress toward your goal', 'Donor confirmation emails'],
  },
}

const EXTERNAL_COPY = {
  title: 'Link your existing page',
  description: 'Send people to the platform you already use.',
  bullets: ['Keep Givebutter, Classy, Eventbrite, or Givergy', 'Nothing to migrate', 'Switch anytime'],
}

export default function PaymentModeCards({
  kind,
  mode,
  onModeChange,
  externalUrl,
  onExternalUrlChange,
  urlError,
}: {
  kind: CampaignKind
  mode: PaymentMode
  onModeChange: (mode: PaymentMode) => void
  externalUrl: string
  onExternalUrlChange: (url: string) => void
  urlError?: string
}) {
  const platform = externalPlatformName(deriveExternalPlatform(externalUrl))
  const linkingLabel = externalUrl.trim()
    ? platform
      ? `Linking to ${platform}`
      : 'Linking to external page'
    : null

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <ModeCard
          active={mode === 'native'}
          onSelect={() => onModeChange('native')}
          icon={<Ticket className="size-5" />}
          {...NATIVE_COPY[kind]}
        />
        <ModeCard
          active={mode === 'external'}
          onSelect={() => onModeChange('external')}
          icon={<Link2 className="size-5" />}
          {...EXTERNAL_COPY}
        />
      </div>
      {mode === 'external' && (
        <div className="space-y-2">
          <Label htmlFor="external_url">
            {kind === 'fundraiser' ? 'Your fundraising page link' : 'Your ticketing page link'}
            <span className="text-destructive"> *</span>
          </Label>
          <Input
            id="external_url"
            type="url"
            value={externalUrl}
            placeholder="https://givebutter.com/your-campaign"
            onChange={(event) => onExternalUrlChange(event.target.value)}
          />
          {linkingLabel && <p className="text-xs text-muted-foreground">{linkingLabel}</p>}
          {urlError && <p className="text-xs text-destructive">{urlError}</p>}
        </div>
      )}
    </div>
  )
}

function ModeCard({
  active,
  onSelect,
  icon,
  title,
  description,
  bullets,
}: {
  active: boolean
  onSelect: () => void
  icon: React.ReactNode
  title: string
  description: string
  bullets: string[]
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`rounded-lg border bg-card p-4 text-left transition ${
        active ? 'border-primary ring-2 ring-primary/30' : 'border-black/10 hover:border-primary/40'
      }`}
    >
      <span
        className={`inline-flex size-10 items-center justify-center rounded-md ${
          active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
        }`}
      >
        {icon}
      </span>
      <span className="mt-4 block font-serif text-lg font-semibold">{title}</span>
      <span className="mt-1 block text-sm text-muted-foreground">{description}</span>
      <span className="mt-3 block space-y-1 text-sm text-muted-foreground">
        {bullets.map((bullet) => (
          <span key={bullet} className="block">+ {bullet}</span>
        ))}
      </span>
    </button>
  )
}
